import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import { AxiosResponse } from "axios";
import FacilityService from "Services/FacilityService/FacilityService";
import PermissionComponent from "Shared/Common/Permissions/PermissionComponent";
import { LoaderIcon } from "Shared/Icons";
import useLang from "Shared/hooks/useLanguage";

interface CellData {
  id: number;
  systemCellName: string;
  customCellName: string;
  customCellOrder: number;
  isActive: boolean;
}

interface InnerSectionTemplateSettingProps {
  innerRow: CellData[];
  setOpen: (open: boolean) => void;
  loadGridData: () => void;
}

const InnerSectionTemplateSetting = (props: InnerSectionTemplateSettingProps) => {
  const { t } = useLang();
  const queryClient = useQueryClient();
  const { innerRow, setOpen, loadGridData } = props;
  const [data, setData] = useState<CellData[]>(innerRow);

  // Sync data with props when innerRow changes
  useEffect(() => {
    setData(innerRow);
  }, [innerRow]);

  // React Query Mutation: Save cells
  const saveCellsMutation = useMutation<AxiosResponse<any>, Error, CellData[]>(
    (cellsData: CellData[]) => FacilityService.SaveCells(cellsData),
    {
      onSuccess: (response) => {
        if (response?.data.statusCode === 200) {
          toast.success(t(response?.data?.message));
          setOpen(false);
          // Invalidate parent queries to refresh data
          queryClient.invalidateQueries(["templateSettings"]);
          loadGridData();
        }
      },
      onError: (error: any) => {
        console.error("Error saving cells:", error);
        toast.error(t("Error saving cells"));
      },
    }
  );

  const handleToggleActive = (row: CellData) => {
    const index = data.findIndex((item) => item.id === row.id);
    if (index !== -1) {
      setData((prevData) => {
        const updated = [...prevData];
        updated[index] = { ...updated[index], isActive: !updated[index].isActive };
        return updated;
      });
    }
  };

  const handleChangeCustomCellName = (id: number, value: string) => {
    const index = data.findIndex((item) => item.id === id);
    if (index !== -1) {
      setData((prevData) => {
        const updated = [...prevData];
        updated[index] = { ...updated[index], customCellName: value };
        return updated;
      });
    }
  };

  const handleChangeCustomCellOrder = (id: number, value: string) => {
    const index = data.findIndex((item) => item.id === id);
    if (index !== -1) {
      const numericValue = value === "" ? 0 : parseInt(value, 10);
      setData((prevData) => {
        const updated = [...prevData];
        updated[index] = { ...updated[index], customCellOrder: isNaN(numericValue) ? 0 : numericValue };
        return updated;
      });
    }
  };

  const handleSubmit = () => {
    saveCellsMutation.mutate(data);
  };

  return (
    <>
      <div className="py-2">
        <div className="d-flex align-items-center gap-2 gap-lg-3 mb-2">
          <button
            id={`IDTemplateSettingExpandCancel`}
            className=" btn btn-secondary btn-sm btn-secondary--icon fw-bold py-2 rounded-3"
            onClick={() => setOpen(false)}
          >
            {t("Cancel")}
          </button>
          <PermissionComponent
            moduleName="ID LIS"
            pageName="Result Data Pre-Configuration"
            permissionIdentifier="AddNewTemplate"
          >
            <button
              id={`IDTemplateSettingExpandSave`}
              className="btn btn-primary btn-sm fw-500 py-2 rounded-3"
              onClick={handleSubmit}
              disabled={saveCellsMutation.isLoading}
            >
              {saveCellsMutation.isLoading ? <LoaderIcon /> : null}
              <span>{t("Save")}</span>
            </button>
          </PermissionComponent>
        </div>
        <div className="table_bordered overflow-hidden">
          <TableContainer className="shadow-none">
            <Table
              // stickyHeader
              aria-label="sticky table collapsible"
              className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
            >
              <TableHead>
                <TableRow>
                  <TableCell className="min-w-200px w-200px">
                    {t("System Cell Name")}
                  </TableCell>
                  <TableCell className="min-w-200px w-200px">
                    {t("Custom Cell Name")}
                  </TableCell>
                  <TableCell className="min-w-200px w-200px">
                    {t("Custom Cell ID")}
                  </TableCell>
                  <TableCell className="min-w-50px w-50px">
                    {t("Exclude/Include")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data && data.length > 0 ? (
                  data.map((row: CellData) => (
                    <TableRow key={row.id}>
                      <TableCell
                        id={`IDTemplateSettingExpandSystemCellName_${row.id}`}
                      >
                        {row.systemCellName}
                      </TableCell>
                      <TableCell>
                        <input
                          id={`IDTemplateSettingExpandCustomCellName_${row.id}`}
                          type="text"
                          className="form-control bg-white h-25px rounded-2 py-0 line-height-0 fs-8 fw-500 px-2"
                          placeholder={t("Custom Cell Name")}
                          name="customCellName"
                          value={row.customCellName || ""}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChangeCustomCellName(row.id, event.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <input
                          id={`IDTemplateSettingExpandCustomCellOrder_${row.id}`}
                          type="text"
                          className="form-control bg-white h-25px rounded-2 py-0 line-height-0 fs-8 fw-500 px-2"
                          placeholder={t("Custom Cell Order")}
                          value={row.customCellOrder || 0}
                          name="customCellOrder"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChangeCustomCellOrder(row.id, event.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="d-flex justify-content-center">
                          <div className="form-check form-switch">
                            <input
                              id={`IDTemplateSettingExpandSwitchButton_${row.id}`}
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              name="isActive"
                              checked={row.isActive || false}
                              onChange={() => handleToggleActive(row)}
                            />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4">
                      <span className="text-muted">{t("No cells available")}</span>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </>
  );
};

export default InnerSectionTemplateSetting;
